import { useCallback } from 'react';
import { AnimatedLoadingIcon, Button } from '@eventbrite/marmalade';
import { useIntl } from 'react-intl';
import { buildEventbriteSignInUrl } from '@/utils/build-eventbrite-signin-url';
import { useFollowContext } from '../useFollowContext';
import { useOrganizerFollowMutation } from './useOrganizerFollowMutation';
import { useOrganizerUnfollowMutation } from './useOrganizerUnfollowMutation';
import { isGDPRRegime, fetchGDPRCommSettings, hasChosenGDPR } from './gdpr-utils';
import { AUTO_FOLLOW_PARAM } from './useAutoFollow';
import styles from './FollowButton.module.scss';

interface Props {
  organizerId: string;
  eventbriteBaseUrl: string;
  appearance?: 'header' | 'empty';
}

export function FollowButton({ organizerId, eventbriteBaseUrl, appearance = 'header' }: Props) {
  const intl = useIntl();
  const {
    authenticated,
    followedByUser,
    setFollowedByUser,
    setNumFollowers,
    userId,
    statusLoading,
    mutationLoading,
    setMutationLoading,
    setShouldShowGDPRModal,
    showNotification,
  } = useFollowContext();
  const followMutation = useOrganizerFollowMutation();
  const unfollowMutation = useOrganizerUnfollowMutation();
  const isLoading = statusLoading || mutationLoading;

  const follow = useCallback(async () => {
    if (!organizerId || mutationLoading || statusLoading) return;
    if (!authenticated) {
      const referrer = new URL(window.location.href);
      referrer.searchParams.set(AUTO_FOLLOW_PARAM, '1');
      window.location.href = buildEventbriteSignInUrl(eventbriteBaseUrl, referrer.toString());
      return;
    }
    setMutationLoading(true);
    try {
      const {
        actionPerformed,
        unauthorized,
        numFollowers: updatedFollowers,
      } = await followMutation.mutateAsync(organizerId);
      if (actionPerformed) {
        setFollowedByUser(true);
        if (typeof updatedFollowers === 'number') {
          setNumFollowers(updatedFollowers);
        } else {
          setNumFollowers((prev) => (typeof prev === 'number' ? prev + 1 : prev));
        }
        showNotification('success');

        if (userId && isGDPRRegime()) {
          try {
            const commSettings = await fetchGDPRCommSettings(userId);
            if (!hasChosenGDPR(commSettings)) {
              setShouldShowGDPRModal(true);
            }
          } catch {
            setShouldShowGDPRModal(true);
          }
        }
      } else if (unauthorized) {
        const referrer = new URL(window.location.href);
        referrer.searchParams.set(AUTO_FOLLOW_PARAM, '1');
        window.location.href = buildEventbriteSignInUrl(eventbriteBaseUrl, referrer.toString());
      }
    } catch {
      showNotification('follow-error');
    } finally {
      setMutationLoading(false);
    }
  }, [
    organizerId,
    authenticated,
    mutationLoading,
    statusLoading,
    eventbriteBaseUrl,
    userId,
    followMutation,
    setFollowedByUser,
    setNumFollowers,
    setMutationLoading,
    setShouldShowGDPRModal,
    showNotification,
  ]);

  const unfollow = useCallback(async () => {
    if (!organizerId || mutationLoading || statusLoading) return;
    setMutationLoading(true);
    try {
      const {
        actionPerformed,
        unauthorized,
        numFollowers: updatedFollowers,
      } = await unfollowMutation.mutateAsync(organizerId);
      if (actionPerformed) {
        setFollowedByUser(false);
        if (typeof updatedFollowers === 'number') {
          setNumFollowers(updatedFollowers);
        } else {
          setNumFollowers((prev) => (typeof prev === 'number' ? Math.max(0, prev - 1) : prev));
        }
      } else if (unauthorized) {
        window.location.href = buildEventbriteSignInUrl(eventbriteBaseUrl, window.location.href);
      }
    } catch {
      showNotification('unfollow-error');
    } finally {
      setMutationLoading(false);
    }
  }, [
    organizerId,
    mutationLoading,
    statusLoading,
    eventbriteBaseUrl,
    unfollowMutation,
    setFollowedByUser,
    setNumFollowers,
    setMutationLoading,
    showNotification,
  ]);

  const buttonLabel = followedByUser
    ? intl.formatMessage({ id: 'Following', defaultMessage: 'Following' })
    : intl.formatMessage({ id: 'Follow', defaultMessage: 'Follow' });

  if (appearance === 'empty') {
    return (
      <div className={styles.followEmptySlot}>
        <Button
          variant="secondary"
          sizeVariant="medium"
          type="button"
          disabled={isLoading}
          onClick={() => void (followedByUser ? unfollow() : follow())}
        >
          {isLoading ? (
            <span className={styles.spinner}>
              <AnimatedLoadingIcon />
            </span>
          ) : (
            buttonLabel
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className={followedByUser ? styles.followFollowingSlot : styles.followPrimarySlot}>
      <Button
        variant={followedByUser ? 'secondary' : 'primary'}
        sizeVariant="large"
        type="button"
        disabled={isLoading}
        onClick={() => void (followedByUser ? unfollow() : follow())}
      >
        {isLoading ? (
          <span className={styles.spinner}>
            <AnimatedLoadingIcon />
          </span>
        ) : (
          buttonLabel
        )}
      </Button>
    </div>
  );
}
